import React from "react";
import { Box, IconButton, Tooltip, Chip } from "@mui/material";
import {
  AdminPanelSettings,
  Visibility as ViewIcon,
  VisibilityOff as NoAccessIcon,
  Edit as EditRoleIcon,
  Lock as LockIcon,
} from "@mui/icons-material";

export const ROLE_OPTIONS = [
  {
    value: "none",
    label: "No access",
    Icon: NoAccessIcon,
    color: "error",
  },
  {
    value: "viewer",
    label: "View only",
    Icon: ViewIcon,
    color: "info",
  },
  {
    value: "admin",
    label: "Full Control",
    Icon: EditRoleIcon,
    color: "secondary",
  },
];

const OPTIONS_MAP = Object.fromEntries(
  ROLE_OPTIONS.map((opt) => [opt.value, opt])
);

export const getRoleConfig = (role) => OPTIONS_MAP[role] || OPTIONS_MAP.none;

export const RoleButtonGroup = ({
  value,
  onChange,
  roles = ["none", "viewer", "admin"],
  disabled = false,
}) => {
  const normalizedValue = value === "editor" ? "admin" : value;
  const options = roles.map((r) => OPTIONS_MAP[r]).filter(Boolean);
  return (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      {options.map((opt) => {
        const isSelected =
          normalizedValue === opt.value ||
          (opt.value === "admin" && value === "editor");
        return (
          <Tooltip key={opt.value} title={opt.label}>
            <IconButton
              size='small'
              disabled={disabled}
              onClick={() => onChange?.(opt.value)}
              sx={{
                bgcolor: isSelected ? `${opt.color}.main` : "transparent",
                color: isSelected ? "#fff" : "text.secondary",
                "&:hover": {
                  bgcolor: isSelected ? `${opt.color}.dark` : "action.hover",
                },
              }}
            >
              <opt.Icon fontSize='small' />
            </IconButton>
          </Tooltip>
        );
      })}
    </Box>
  );
};

export const RoleChip = ({ role, size = "small", isInactive = false }) => {
  // Normalize: treat "editor" role as "admin"
  const normalizedRole = role === "editor" ? "admin" : role;
  const config = getRoleConfig(normalizedRole);
  const Icon = config.Icon;
  return (
    <Tooltip
      title={
        isInactive
          ? "You cannot change permissions for an inactive user."
          : config.label
      }
    >
      <Chip
        icon={
          isInactive ? <LockIcon fontSize='small' /> : <Icon fontSize='small' />
        }
        label={
          config.label
            ? config.label.charAt(0).toUpperCase() + config.label.slice(1)
            : ""
        }
        size={size}
        color={config.color}
        variant={normalizedRole === "none" ? "outlined" : "filled"}
        sx={{
          fontWeight: 500,
          opacity: normalizedRole === "none" ? 0.7 : 1,
          bgcolor: normalizedRole === "none" ? "transparent" : undefined,
        }}
      />
    </Tooltip>
  );
};
